<script setup lang="ts">
import { ref, watch, watchEffect, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Filter, SearchQuery } from '../../types';
import { getQueryObject, getQueryString } from '../../query-formatters';
import { VTextField } from 'vuetify/components/VTextField';
import useLocale from '../../composables/useLocale';
import { useDisplay } from 'vuetify';

type Props = {
  filterDefinitions?: Filter;
  placeholder: string;
  focused?: boolean;
  enableSearchQuery?: boolean;
};
type Emits = {
  (e: 'submit', query: SearchQuery): void;
  (e: 'reset'): void;
};
const { t } = useLocale();
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const router = useRouter();
const route = useRoute();
const { smAndDown, platform, md, lg, xlAndUp } = useDisplay();
const context = ref<SearchQuery>({ q: '' });

watchEffect(() => {
  context.value = Object.keys(route.query).length > 0 ? getQueryObject(window.location.search.slice(1)) : { q: '' };
});
const searchTerm = ref(context.value.q);
const inputRef = ref<HTMLInputElement>();

const updateContext = (query: SearchQuery) => {
  const queryString = getQueryString(query);
  const redirectPath = `${route.path}${queryString}`;

  if (props.enableSearchQuery && route.fullPath !== redirectPath) {
    router.push(redirectPath);
  }
};
const handleReset = () => {
  searchTerm.value = '';
  updateContext({
    ...context.value,
    q: '',
  });
  emit('reset');
};
const handleEscape = () => {
  handleReset();
  inputRef.value?.blur();
};

const handleSubmit = () => {
  updateContext({
    ...context.value,
    q: searchTerm.value,
  });
};

const handleKeyPress = (event: KeyboardEvent) => {
  switch (event.keyCode) {
    case 13: // enter
      handleSubmit();
      return;
    case 27: // escape
      handleEscape();
      break;
    default:
    // ignore
  }
};
const triggerSearch = () => {
  emit('submit', context.value);
};
watch(
  context,
  () => {
    let shouldSearch = false;
    if (context.value.q && context.value.q!.length > 0) {
      shouldSearch = true;
    }
    if (shouldSearch) {
      triggerSearch();
    } else {
      handleReset();
    }
  },
  { immediate: true },
);

const setFocus = () => {
  if (inputRef.value) {
    inputRef.value.focus();
  }
};

onMounted(() => {
  /*
  We want to autofocus the search input but on mobile apps the keyboard takes
  too much space, so we only focus on web.
 */
  if (props.focused && !platform.value.touch) {
    setFocus();
  }
});
</script>

<template>
  <div class="search-input-container">
    <!-- @vue-ignore FIXME I don't know how to specify types for bindable attrs from top level-->
    <v-text-field
      id="search-input"
      ref="inputRef"
      v-model="searchTerm"
      type="search"
      v-bind="{ ...$props, ...$attrs }"
      :placeholder="placeholder"
      variant="solo"
      :clearable="true"
      class="search-bar elevation-0"
      base-color="on-surface-low"
      color="on-surface"
      bg-color="surface-bright"
      active
      :aria-label="t('content_search.label_search_bar')"
      aria-describedby="search-input"
      @keydown="handleKeyPress"
    >
      <template v-if="smAndDown" #prepend-inner>
        <v-icon>mdi-magnify</v-icon>
      </template>
      <template #label>
        <span class="d-none">{{ t('content_search.label_search_bar') }}</span>
      </template>
      <template #append-inner>
        <v-icon v-if="md" class="mr-4"></v-icon>
        <v-icon v-if="lg" class="mr-10"></v-icon>
        <v-icon v-if="xlAndUp" class="mr-16"></v-icon>
        <v-btn
          size="large"
          class="submit-btn d-none d-md-flex text-on-primary rounded-e-pill"
          color="primary"
          variant="flat"
          icon
          :aria-label="t('content_search.search_button')"
          @click="handleSubmit"
        >
          <v-icon size="large">mdi-magnify</v-icon>
        </v-btn>
      </template>
    </v-text-field>
    <!-- Visible only md+ -->
  </div>
</template>

<style lang="scss" scoped>
@import 'vuetify/settings';

.search-input-container {
  width: 100%;
  position: relative;
}
:deep(.v-field) {
  outline: transparent solid 2px;
}
:deep(.v-field),
:deep(.v-field__field) {
  max-height: 48px;
  @media #{map-get($display-breakpoints, 'md-and-up')} {
    padding-left: 8px;
  }
}
:deep(.v-field) {
  border: 2px solid rgb(var(--v-theme-outline-variant));
  padding-right: 0;
}

:deep(.v-field--focused) {
  border: 2px solid rgb(var(--v-theme-primary));
}
:deep(.v-field--variant-solo) {
  box-shadow: none;
}
.submit-btn {
  position: absolute;
  right: 0;
  top: 0;
}
.field {
  * > label {
    font-size: 2em;
  }
}
:deep(.v-text-field input) {
  padding: 9px 0 12px;
}
:deep(input) {
  outline: none !important;
}
.v-btn--icon.v-btn--density-default {
  width: 4% !important;
  height: 100% !important;
}
</style>
